import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { object, string, number, boolean, mixed } from 'yup';

import Cabecalho from 'Component/Admin/Cabecalho';
import { useToast } from 'Component/Alerta';
import Botao from 'Component/Botao';
import BotaoLink from 'Component/BotaoLink';
import Form, { Arquivo, Botao as BotaoForm, CaixaDeSelecao, CaixaDeTexto, Campo, Contexto, Selecao } from 'Component/Form';
import Icone from 'Component/Icone';
import Imagem from 'Component/Imagem';
import Curso from 'Interface/Cursos';
import { useEditarCursoMutation, useRecuperarCategoriasQuery, useRecuperarCursoQuery } from 'Service';
import { CARGA_HORARIA, TECNOLOGIAS, TIPOS_CURSOS_IESDE } from 'View/constants';

import Aulas from './Aulas';
import Avaliacoes from './Avaliacoes';

const regras = object({
    nome: string().required('Obrigatório'),
    categoria_id: number().required('Obrigatório'),
    tecnologia: string().required('Obrigatório'),
    nome_lti: string().nullable().optional(),
    codigo: string().nullable().optional(),
    tipo_iesde: string().nullable().optional(),
    descricao: string().nullable().optional(),
    objetivo: string().nullable().optional(),
    autores: string().nullable().optional(),
    publico_alvo: string().nullable().optional(),
    carga_horaria: number().positive().required('Obrigatório'),
    visivel: boolean().nullable(),
    destaque: boolean().nullable(),
    imagem: mixed()
        .test('tamanho', 'O arquivo é muito grande', valor => {
            if (valor && valor.size) {
                return valor.size <= 500000;
            }
            return true;
        })
        .test('tipo', 'Formato não suportado', valor => {
            if (valor && valor.type) {
                return ['image/png', 'image/jpeg', 'image/webp'].includes(valor.type)
            }
            return true;
        })
        .optional(),
});

const Editar = (): React.ReactElement =>
{
    const navegacao = useNavigate();
    const toast = useToast();
    const [ aba, setAba ] = useState<number>(0);
    const { id = 0 } = useParams<'id'>();
    const { data: { curso } = {}, isLoading } = useRecuperarCursoQuery(id);
    const { data: { categorias } = {} } = useRecuperarCategoriasQuery({ finder: 'listaJson' });
    const [ editarCurso ] = useEditarCursoMutation();

    const enviar = async (dados: Curso): Promise<void> =>
    {
        try {
            await editarCurso(dados).unwrap();

            toast({ conteudo: 'Editado com sucesso!', tema: 'success' });

            navegacao('/admin/cursos');
        } catch (error) {
            toast({
                titulo: 'Erro ao editar',
                conteudo: 'Verifique os logs para maiores detalhes.',
                tema: 'danger',
            });
        }
    }

    return (
        <div className="animate__animated animate__fadeIn">
            <Cabecalho
                titulo="Editando usuário"
                descricao="Altere o formulário e clique em enviar para editar o usuário"
            />
            <div className="row">
                <div className="col-md-1 vstack flex-row flex-sm-column gap-2 mb-3">
                    <Botao
                        icone="house"
                        tema={ aba == 0 ? 'primary' : 'outline-primary' }
                        onClick={ () => setAba(0) }
                    />
                    <Botao
                        icone="collection-play-fill"
                        tema={ aba == 1 ? 'primary' : 'outline-primary' }
                        onClick={ () => setAba(1) }
                    />
                    <Botao
                        icone="journal-text"
                        tema={ aba == 2 ? 'primary' : 'outline-primary' }
                        onClick={ () => setAba(2) }
                    />
                </div>
                <div className="col-md-11 z-index-5000">
                    { aba == 0 && (
                        <Form onSubmit={ enviar } desabilitado={ isLoading } inicial={ curso } regras={ regras }>
                            <div className="row animate__animated animate__fadeIn">
                                <div className="col-md-9">
                                    <div className="bg-white shadow-sm rounded p-4">
                                        <div className="row">
                                            <div className="col-md-12">
                                                <Campo name="nome" rotulo="Nome" />
                                            </div>
                                            <div className="col-md-12">
                                                <Campo
                                                    name="slug"
                                                    rotulo="Slug para URL amigável"
                                                    prefixar={<Icone nome="globe" />}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <Selecao
                                                    name="categoria_id"
                                                    rotulo="Categoria"
                                                    opcoes={ categorias }
                                                    prefixar={<Icone nome="view-list" />}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <Selecao
                                                    name="tecnologia"
                                                    rotulo="Tecnologia"
                                                    opcoes={ TECNOLOGIAS }
                                                    prefixar={<Icone nome="cpu" />}
                                                />
                                            </div>
                                            <Contexto>
                                                { ({ values }) => (
                                                    <React.Fragment>
                                                        { values.tecnologia == 'lti' && (
                                                            <div className="col-md-12">
                                                                <Campo
                                                                    name="nome_lti"
                                                                    rotulo="Nome do recurso LTI"
                                                                    ajuda="Deve ser exatamente o mesmo nome cadastro na plataforma LTI"
                                                                />
                                                            </div>
                                                        ) }
                                                        { values.tecnologia == 'iesde' && (
                                                            <React.Fragment>
                                                                <div className="col-md-6">
                                                                    <Campo
                                                                        name="codigo"
                                                                        rotulo="Código do curso"
                                                                        ajuda="Código que idêntifica o curso na plataforma do provedor"
                                                                    />
                                                                </div>
                                                                <div className="col-md-6">
                                                                    <Selecao
                                                                        name="tipo_iesde"
                                                                        rotulo="Tipo do curso"
                                                                        opcoes={ TIPOS_CURSOS_IESDE }
                                                                    />
                                                                </div>
                                                            </React.Fragment>
                                                        ) }
                                                    </React.Fragment>
                                                ) }
                                            </Contexto>
                                            <div className="col-md-12">
                                                <CaixaDeTexto name="descricao" rotulo="Descrição" />
                                            </div>
                                            <div className="col-md-6">
                                                <CaixaDeTexto name="objetivo" rotulo="Objetivo" />
                                            </div>
                                            <div className="col-md-6">
                                                <CaixaDeTexto name="autores" rotulo="Autores" />
                                            </div>
                                            <div className="col-md-12">
                                                <CaixaDeTexto name="publico_alvo" rotulo="Público alvo" semMargem />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="bg-white shadow-sm rounded p-4">
                                        <Imagem
                                            src={ curso?.imagem }
                                            className="rounded shadow-lg w-100 object-fit-cover mb-5"
                                            height="200"
                                        />

                                        <Arquivo name="imagem" />
                                        <Selecao name="carga_horaria" rotulo="Carga horária" opcoes={ CARGA_HORARIA } />
                                        <CaixaDeSelecao name="visivel" rotulo="Visível para compra" />
                                        <CaixaDeSelecao name="destaque" rotulo="Aparece em destaque" />

                                        <div className="mt-5">
                                            <BotaoForm>Enviar</BotaoForm>
                                            <BotaoLink tema="light" to="/admin/cursos" className="ms-2">
                                                Cancelar
                                            </BotaoLink>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Form>
                    ) }
                    { aba == 1 && (
                        <Aulas />
                    ) }
                    { aba == 2 && (
                        <Avaliacoes />
                    ) }
                </div>
            </div>
        </div>
    )
}

export default Editar;
